import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Header,
  SpaceBetween,
  Button,
  Alert,
  BreadcrumbGroup,
  Form,
  FormField,
  Input,
  Textarea,
  FileUpload,
  Select,
  StatusIndicator,
  Icon,
} from '@cloudscape-design/components';
import axios from 'axios';
import AppLayoutWrapper from './AppLayoutWrapper';
import { useAuth } from '../AuthContext';
import { useFeatureFlags } from '../contexts/FeatureFlagsContext';

export default function KnowledgeBaseAdd() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isEnabled } = useFeatureFlags();
  const knowledgeBaseEnabled = isEnabled('knowledge_base');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  // Hidden defaults (same as agent wizard)
  const platformMode = { label: 'Dedicated', value: 'dedicated' };
  const billingCycle = { label: 'Yearly', value: 'yearly' };
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [quota, setQuota] = useState({ total: 0, used: 0, remaining: 0, loading: true });

  const kbQuotaAvailable = quota.remaining > 0;
  const canCreateKb = knowledgeBaseEnabled && kbQuotaAvailable;

  // Assignment mode: directly to an agent or reserved on a pack
  const [assignmentMode, setAssignmentMode] = useState({ label: 'Reserve on a pack', value: 'pack' });
  const [agents, setAgents] = useState([]);
  const [packs, setPacks] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [selectedPack, setSelectedPack] = useState(null);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [loadingPacks, setLoadingPacks] = useState(false);

  const accessToken = user?.access_token || user?.token;

  useEffect(() => {
    const fetchAgents = async () => {
      if (!accessToken) return;
      setLoadingAgents(true);
      try {
        const resp = await axios.get('/api/agents/for-assignment/', {
          headers: { Authorization: `Bearer ${accessToken}` }
        });
        if (resp.data?.status === 'success' && Array.isArray(resp.data.data)) {
          setAgents(resp.data.data.map(a => ({ label: a.name || a.id, value: a.id })));
        }
      } catch (err) {
        console.error('Error fetching agents:', err);
      } finally {
        setLoadingAgents(false);
      }
    };

    const fetchPacks = async () => {
      if (!accessToken) return;
      setLoadingPacks(true);
      try {
        const resp = await axios.get('/api/company-packs/', {
          headers: { Authorization: `Bearer ${accessToken}` }
        });
        if (resp.data?.status === 'success' && Array.isArray(resp.data.data)) {
          setPacks(resp.data.data
            .filter(p => p.quotas?.knowledge_bases?.remaining > 0)
            .map(p => {
              const kbTotal = p.quotas?.knowledge_bases?.total || 0;
              const kbUsed = p.quotas?.knowledge_bases?.used || 0;
              const kbRemaining = p.quotas?.knowledge_bases?.remaining || 0;
              const statusLabel = p.status === 'inventory' ? 'In stock' : (p.has_agent ? 'Assigned' : 'Active');
              return {
                label: `${p.name} (${kbUsed}/${kbTotal} KB) — ${statusLabel}`,
                value: p.id
              };
            }));
        }
      } catch (err) {
        console.error('Error fetching packs:', err);
      } finally {
        setLoadingPacks(false);
      }
    };

    const fetchQuota = async () => {
      if (!accessToken) return;
      try {
        const resp = await axios.get('/api/billing/quota/', {
          headers: { Authorization: `Bearer ${accessToken}` }
        });
        const resources = resp.data?.data?.resources || {};
        const kb = resources.knowledge_bases || { total: 0, used: 0, remaining: 0 };
        setQuota({ total: kb.total, used: kb.used, remaining: kb.remaining, loading: false });
      } catch (err) {
        console.error('Error fetching quota:', err);
        setQuota(prev => ({ ...prev, loading: false }));
      }
    };

    fetchAgents();
    fetchPacks();
    fetchQuota();
  }, [accessToken]);

  const handleSubmit = async () => {
    if (!name.trim()) {
      setError('Knowledge base name is required');
      return;
    }
    if (assignmentMode.value === 'agent' && !selectedAgent) {
      setError('Please select an agent');
      return;
    }
    if (assignmentMode.value === 'pack' && !selectedPack) {
      setError('Please select a pack');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const configJson = {
        chunking: {
          chunk_size: 1000,
          chunk_overlap: 200
        }
      };
      const payload = {
        name: name.trim(),
        description: description.trim(),
        platform_mode: platformMode.value,
        billing_cycle: billingCycle.value,
        config_json: configJson,
      };
      if (assignmentMode.value === 'agent') {
        payload.agent_id = selectedAgent.value;
      } else {
        payload.company_pack_id = selectedPack.value;
      }
      const resp = await axios.post('/api/knowledge-bases/create/', payload, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      let createResp = resp.data;
      if (createResp?.status === 'confirmation_required') {
        payload.confirm_resource_activation = true;
        const confirmResp = await axios.post('/api/knowledge-bases/create/', payload, {
          headers: { Authorization: `Bearer ${accessToken}` }
        });
        createResp = confirmResp.data;
      }
      if (createResp?.status === 'sync_failed') {
        setError(createResp.message || 'KB created locally but failed to sync to adexcloud. You can retry sync from the KB list.');
        setSuccess(false);
        setTimeout(() => navigate('/knowledge-bases'), 3000);
        return;
      }
      const kbId = createResp?.data?.id;

      // Upload files if any
      if (files.length > 0 && kbId) {
        const uploadFileIds = [];
        for (const file of files) {
          const formData = new FormData();
          formData.append('file', file);
          const uploadResp = await axios.post(`/api/knowledge-bases/${kbId}/upload/`, formData, {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              'Content-Type': 'multipart/form-data',
            },
          });
          const serverFileId = uploadResp.data?.data?.file_id;
          if (serverFileId) uploadFileIds.push(serverFileId);
        }

        // Poll for file processing status
        if (uploadFileIds.length > 0) {
          setLoading(true);
          const pollStatus = () =>
            new Promise((resolve) => {
              let elapsed = 0;
              const maxWait = 300000; // 5 minutes max
              const interval = setInterval(async () => {
                elapsed += 3000;
                try {
                  const results = await Promise.all(
                    uploadFileIds.map((fid) =>
                      axios.get(`/api/knowledge-bases/${kbId}/files/${fid}/status/`, {
                        headers: { Authorization: `Bearer ${accessToken}` },
                      })
                    )
                  );
                  const allDone = results.every((r) => {
                    const s = r.data?.data?.status;
                    return s === 'success' || s === 'completed' || s === 'failed';
                  });
                  if (allDone) {
                    clearInterval(interval);
                    resolve();
                  } else if (elapsed >= maxWait) {
                    console.warn('Polling timed out after 5 minutes');
                    clearInterval(interval);
                    resolve();
                  }
                } catch (err) {
                  console.error('Status poll error:', err);
                  clearInterval(interval);
                  resolve();
                }
              }, 3000);
            });
          await pollStatus();
        }
      }

      setSuccess(true);
      setTimeout(() => navigate('/knowledge-bases'), 1200);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to create knowledge base');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AppLayoutWrapper
      breadcrumbs={
        <BreadcrumbGroup
          items={[
            { text: 'Home', href: '/dashboard' },
            { text: 'Knowledge Bases', href: '/knowledge-bases' },
            { text: 'Add Knowledge Base', href: '/knowledge-base/new' },
          ]}
          onFollow={(event) => {
            event.preventDefault();
            navigate(event.detail.href);
          }}
        />
      }
      header={{
        title: 'Add Knowledge Base',
        description: 'Create a new knowledge base and upload documents.',
      }}
      headerBackgroundStyle="linear-gradient(to bottom, rgba(9, 29, 43, 1) 2%, rgba(0, 108, 224, 1) 8%, rgba(255, 255, 255, 1) 82%)"
      helpPanelHeader="Knowledge Bases"
      helpPanelContent={
        <div>
          <p>Knowledge bases provide contextual documents for your agents.</p>
          <ul>
            <li>Upload PDFs, docs, and text files</li>
            <li>Attach KBs to one or more agents</li>
            <li>Monitor ingestion status</li>
          </ul>
        </div>
      }
    >
      <SpaceBetween size="l">
        {success && (
          <Alert type="success" header="Knowledge base created">
            Redirecting to list...
          </Alert>
        )}
        {!knowledgeBaseEnabled && (
          <Alert type="warning" header="Feature disabled">
            Knowledge bases are currently disabled by the administrator.
          </Alert>
        )}
        {!quota.loading && !kbQuotaAvailable && knowledgeBaseEnabled && (
          <Alert type="warning" header="Quota exceeded">
            Knowledge base quota exceeded ({quota.used}/{quota.total}). Purchase a pack with more knowledge bases.
          </Alert>
        )}
        {error && <Alert type="error" header="Error">{error}</Alert>}

        <Container header={<Header variant="h2"><Icon name="file" variant="normal" /> Knowledge base configuration</Header>}>
          <Form
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Button variant="link" onClick={() => navigate('/knowledge-bases')}>
                  Cancel
                </Button>
                <Button variant="primary" onClick={handleSubmit} loading={loading} disabled={!canCreateKb}>
                  Create Knowledge Base
                </Button>
              </SpaceBetween>
            }
          >
            <SpaceBetween size="l">
              <Container header={<Header variant="h3">General</Header>}>
                <SpaceBetween size="l">
                  <FormField label="Name" constraintText="Required">
                    <Input value={name} onChange={({ detail }) => setName(detail.value)} placeholder="e.g., Company FAQ" />
                  </FormField>
                  <FormField label="Description">
                    <Textarea
                      value={description}
                      onChange={({ detail }) => setDescription(detail.value)}
                      placeholder="What does this knowledge base contain?"
                    />
                  </FormField>
                </SpaceBetween>
              </Container>

              <Container header={<Header variant="h3">Assignment</Header>}>
                <SpaceBetween size="l">
                  <FormField label="Assign To" description="Associate this knowledge base with an existing agent or reserve it on a pack.">
                    <Select
                      selectedOption={assignmentMode}
                      onChange={({ detail }) => {
                        setAssignmentMode(detail.selectedOption);
                        setSelectedAgent(null);
                        setSelectedPack(null);
                      }}
                      options={[
                        { label: 'Reserve on a pack', value: 'pack' },
                        { label: 'Existing agent', value: 'agent' },
                      ]}
                    />
                  </FormField>
                  {assignmentMode.value === 'agent' && (
                    <FormField label="Select Agent" description="Choose an agent that will use this knowledge base.">
                      <Select
                        selectedOption={selectedAgent}
                        onChange={({ detail }) => setSelectedAgent(detail.selectedOption)}
                        options={agents}
                        loading={loadingAgents}
                        placeholder="Select an agent"
                      />
                    </FormField>
                  )}
                  {assignmentMode.value === 'pack' && (
                    <FormField label="Select Pack" description="Reserve this knowledge base on a pack. It can be assigned to an agent later, but must match the same pack.">
                      <Select
                        selectedOption={selectedPack}
                        onChange={({ detail }) => setSelectedPack(detail.selectedOption)}
                        options={packs}
                        loading={loadingPacks}
                        placeholder="Select a pack"
                      />
                    </FormField>
                  )}
                </SpaceBetween>
              </Container>

              <Container header={<Header variant="h3">Documents</Header>}>
                <FormField label="Upload files (optional)">
                  <FileUpload
                    onChange={({ detail }) => setFiles(detail.value)}
                    value={files}
                    i18nStrings={{
                      uploadButtonText: (e) => (e ? 'Choose files' : 'Choose file'),
                      dropzoneText: (e) =>
                        e ? 'Drop files to upload' : 'Drop file to upload',
                      removeFileAriaLabel: (e) => `Remove file ${e + 1}`,
                      errorIconAriaLabel: 'Error',
                    }}
                    multiple
                    showFileLastModified
                    showFileSize
                    showFileThumbnail
                    tokenLimit={3}
                  />
                </FormField>
              </Container>
              {loading && <StatusIndicator type="loading">{files.length > 0 ? 'Uploading and processing files...' : 'Creating...'}</StatusIndicator>}
            </SpaceBetween>
          </Form>
        </Container>
      </SpaceBetween>
    </AppLayoutWrapper>
  );
}
